import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { contactsAPI, type Contact } from '../services/api';
import { Button, Avatar, StageBadge, type ContactStage } from '../components/ui';
import { ContactForm, CSVImportModal, SortingGame, CleanupBanner, CleanupModal } from '../components/contacts';

const STAGES = [
  { value: '', label: 'All Stages' },
  { value: 'on_deck', label: 'On Deck' },
  { value: 'at_bat', label: 'At Bat' },
  { value: 'on_base', label: 'On Base' },
  { value: 'scoring_position', label: 'Scoring Position' },
  { value: 'hall_of_fame', label: 'Hall of Fame' },
  { value: 'bench', label: 'Bench' },
];

export default function Contacts() {
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSortingGame, setShowSortingGame] = useState(false);
  const [showCleanupModal, setShowCleanupModal] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['contacts', { search, stage: stageFilter }],
    queryFn: () => contactsAPI.list({ search: search || undefined, stage: stageFilter || undefined }),
  });

  const contacts = data?.data?.contacts || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Contacts</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base">
            Manage your sphere of influence
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" className="sm:hidden" onClick={() => setShowCleanupModal(true)}>
            Clean Up
          </Button>
          <Button variant="ghost" className="hidden sm:inline-flex" onClick={() => setShowCleanupModal(true)}>
            Clean Up
          </Button>
          <Button variant="ghost" size="sm" className="sm:hidden" onClick={() => setShowSortingGame(true)}>
            Sort
          </Button>
          <Button variant="ghost" className="hidden sm:inline-flex" onClick={() => setShowSortingGame(true)}>
            Sort Contacts
          </Button>
          <Button variant="outline" size="sm" className="sm:hidden" onClick={() => setShowImportModal(true)}>
            Import
          </Button>
          <Button variant="outline" className="hidden sm:inline-flex" onClick={() => setShowImportModal(true)}>
            Import CSV
          </Button>
          <Button size="sm" className="sm:hidden" onClick={() => setShowAddModal(true)}>
            + Add
          </Button>
          <Button className="hidden sm:inline-flex" onClick={() => setShowAddModal(true)}>
            + Add Contact
          </Button>
        </div>
      </div>

      {/* Post-Import Cleanup Banner */}
      <CleanupBanner onStartCleanup={() => setShowCleanupModal(true)} />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search contacts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
          />
        </div>

        {/* Stage Filter */}
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
        >
          {STAGES.map((stage) => (
            <option key={stage.value} value={stage.value}>
              {stage.label}
            </option>
          ))}
        </select>
      </div>

      {/* Stage Pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        {STAGES.filter(s => s.value).map((stage) => (
          <button
            key={stage.value}
            onClick={() => setStageFilter(stageFilter === stage.value ? '' : stage.value)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              stageFilter === stage.value
                ? 'bg-ondeck-100 dark:bg-ondeck-900/50 text-ondeck-800 dark:text-ondeck-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {stage.label}
          </button>
        ))}
      </div>

      {/* Contacts List */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-ondeck-600 mx-auto"></div>
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Error loading contacts. Please try again.</p>
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="text-4xl mb-4">👥</div>
          <p className="text-gray-600 dark:text-gray-400">No contacts found.</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
            {search || stageFilter
              ? 'Try adjusting your filters.'
              : 'Add your first contact to get started.'}
          </p>
          {!search && !stageFilter && (
            <Button onClick={() => setShowAddModal(true)} className="mt-4">
              Add Contact
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {contacts.map((contact: Contact) => (
              <ContactRow key={contact.id} contact={contact} />
            ))}
          </ul>
        </div>
      )}

      {/* Add Contact Modal */}
      <ContactForm open={showAddModal} onClose={() => setShowAddModal(false)} />

      {/* CSV Import Modal */}
      <CSVImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => setShowSortingGame(true)}
      />

      {/* Sorting Game Modal */}
      <SortingGame open={showSortingGame} onClose={() => setShowSortingGame(false)} />

      {/* AI Cleanup Modal */}
      <CleanupModal open={showCleanupModal} onClose={() => setShowCleanupModal(false)} />
    </div>
  );
}

function ContactRow({ contact }: { contact: Contact }) {
  const contactName = `${contact.first_name} ${contact.last_name || ''}`.trim();

  return (
    <li>
      <Link
        to={`/contacts/${contact.id}`}
        className="block px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Avatar name={contactName} size="md" />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900 dark:text-white">{contactName}</span>
                {contact.company && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">• {contact.company}</span>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                {contact.email && <span>{contact.email}</span>}
                {contact.email && contact.phone && <span>•</span>}
                {contact.phone && <span>{contact.phone}</span>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <StageBadge stage={contact.stage as ContactStage} />
            <div className="text-right text-sm">
              <span className="text-gray-500 dark:text-gray-400">
                {contact.last_contact_at
                  ? `Last: ${formatDate(contact.last_contact_at)}`
                  : 'Never contacted'}
              </span>
            </div>
            <span className="text-gray-400 dark:text-gray-500">→</span>
          </div>
        </div>
      </Link>
    </li>
  );
}

function formatDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return date.toLocaleDateString();
}
